import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Home, 
  Shield, 
  FileCheck, 
  Search, 
  Eye, 
  Download, 
  RefreshCw,
  Settings,
  User,
  Database,
  Server,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const generateConfigurationData = () => {
  const changeTypes = [
    'User permissions updated',
    'System settings modified',
    'Database configuration changed',
    'Security policy updated',
    'API endpoint configuration',
    'Backup settings modified',
    'Network configuration',
    'Authentication settings',
    'Audit rules updated',
    'Data retention policy'
  ];

  const components = ['Authentication', 'Database', 'API Gateway', 'User Management', 'Security', 'Backup', 'Monitoring'];
  const users = ['admin@hospital.com', 'system.admin', 'john.doe', 'config.manager'];

  return Array.from({ length: 200 }, (_, i) => {
    const changeType = changeTypes[Math.floor(Math.random() * changeTypes.length)];
    const component = components[Math.floor(Math.random() * components.length)];
    const user = users[Math.floor(Math.random() * users.length)];
    const timestamp = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);
    const isApproved = Math.random() > 0.1;
    
    return {
      id: `config-${i + 1}`,
      timestamp,
      changeType,
      component,
      user,
      description: `${changeType} in ${component} component`,
      oldValue: 'Previous configuration value',
      newValue: 'Updated configuration value',
      isApproved,
      approvedBy: isApproved ? 'admin@hospital.com' : null,
      riskLevel: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)],
      rollbackAvailable: Math.random() > 0.2,
    };
  }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

export function ConfigurationAudit() {
  const [configData] = useState(generateConfigurationData());
  const [selectedChange, setSelectedChange] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [filters, setFilters] = useState({
    component: 'all',
    riskLevel: 'all',
    status: 'all',
    searchQuery: '',
  });

  const filteredData = useMemo(() => {
    return configData.filter(change => {
      if (dateRange.from && dateRange.to) {
        if (change.timestamp < dateRange.from || change.timestamp > dateRange.to) {
          return false;
        }
      }
      if (filters.component !== 'all' && change.component !== filters.component) return false;
      if (filters.riskLevel !== 'all' && change.riskLevel !== filters.riskLevel) return false;
      if (filters.status !== 'all') {
        if (filters.status === 'approved' && !change.isApproved) return false;
        if (filters.status === 'pending' && change.isApproved) return false;
      }
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return change.changeType?.toLowerCase().includes(query) || change.user?.toLowerCase().includes(query);
      }
      return true;
    });
  }, [configData, dateRange, filters]);

  const summaryStats = useMemo(() => {
    const total = filteredData.length;
    const pending = filteredData.filter(c => !c.isApproved).length;
    const highRisk = filteredData.filter(c => c.riskLevel === 'High').length;
    return { total, pending, highRisk };
  }, [filteredData]);

  const getRiskLevelBadge = (level: string) => {
    const colorMap = {
      'Low': 'bg-green-100 text-green-800 border-green-200',
      'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'High': 'bg-red-100 text-red-800 border-red-200'
    };
    return <Badge variant="outline" className={colorMap[level as keyof typeof colorMap]}>{level}</Badge>;
  };

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Admin Audit
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Configuration Audit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                Configuration Audit Trail
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Track and monitor all system configuration changes
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => toast.success('Configuration data refreshed')}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => toast.success('Configuration audit exported')}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Settings className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.total}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Total Changes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.pending}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Pending Approval</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.highRisk}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>High Risk Changes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="px-8 pb-6">
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <DateRangeFilter dateRange={dateRange} onDateRangeChange={setDateRange} />
                <Input
                  placeholder="Search changes..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                />
                <Select value={filters.component} onValueChange={(value) => setFilters(prev => ({ ...prev, component: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Components</SelectItem>
                    <SelectItem value="Authentication">Authentication</SelectItem>
                    <SelectItem value="Database">Database</SelectItem>
                    <SelectItem value="API Gateway">API Gateway</SelectItem>
                    <SelectItem value="Security">Security</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.riskLevel} onValueChange={(value) => setFilters(prev => ({ ...prev, riskLevel: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risk Levels</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex-1 px-8 pb-8">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>Configuration Changes ({filteredData.length} entries)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead style={{ fontSize: '11.2px' }}>Timestamp</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Change Type</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Component</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>User</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Risk Level</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Status</TableHead>
                    <TableHead style={{ fontSize: '11.2px' }}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.slice(0, 50).map((change) => (
                    <TableRow key={change.id} className={!change.isApproved ? 'bg-yellow-50' : ''}>
                      <TableCell style={{ fontSize: '11.2px' }}>
                        {change.timestamp.toLocaleString()}
                      </TableCell>
                      <TableCell style={{ fontSize: '11.2px', fontWeight: 500 }}>
                        {change.changeType}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Server className="w-4 h-4 text-gray-400" />
                          <span style={{ fontSize: '11.2px' }}>{change.component}</span>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '11.2px' }}>
                        {change.user}
                      </TableCell>
                      <TableCell>
                        {getRiskLevelBadge(change.riskLevel || 'Low')}
                      </TableCell>
                      <TableCell>
                        {change.isApproved ? (
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                            Approved
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => {
                          setSelectedChange(change);
                          setShowDetails(true);
                        }}>
                          <Eye className="w-3 h-3 mr-1" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Configuration Change Details</DialogTitle>
            </DialogHeader>
            {selectedChange && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Change Type</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedChange.changeType}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Component</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedChange.component}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Modified By</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedChange.user}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Risk Level</p>
                    {getRiskLevelBadge(selectedChange.riskLevel)}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-600 mb-2" style={{ fontSize: '10px' }}>Previous Value</p>
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <p style={{ fontSize: '11.2px' }}>{selectedChange.oldValue}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-2" style={{ fontSize: '10px' }}>New Value</p>
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <p style={{ fontSize: '11.2px' }}>{selectedChange.newValue}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}